import React, { useEffect, useState } from 'react'
import { Clapperboard, Sparkles, Mic, Trash2, ArrowUp, ArrowDown, Plus, Loader2, KeyRound, Save, Loader, Upload, Download, Copy, HelpCircle, Scissors } from 'lucide-react'
import { genId } from '../lib/id'
import { videoApi, veoApi, aiSettingsApi, narrateSection, type BankVideo } from '../api/lessonBuilderApi'
import { exportScenesToMp4 } from '../lib/videoExport'

const VEO_MODELS: { id: string; label: string }[] = [
  { id: 'veo-3.1-fast-generate-preview', label: 'Veo 3.1 Fast — rẻ hơn (~$0.15/giây)' },
  { id: 'veo-3.1-generate-preview', label: 'Veo 3.1 Standard — đẹp hơn (~$0.40/giây)' },
]
import { useLessonBuilder } from '../store/useLessonBuilder'
import SimulationLibrary from '../../../services/simulation/SimulationLibrary'
import ScenePlayer from '../components/ScenePlayer'
import type { VideoLectureCard, LectureScene, LessonDoc } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

// Gom nội dung CHỮ từ tài liệu Word của bài (các thẻ) để AI bám sát khi sinh kịch bản.
const extractLessonText = (lesson: LessonDoc): string => {
  const out: string[] = []
  const SKIP = new Set(['id', 'type', 'src', 'imageSrc', 'audioUrl', 'url', 'href', 'color', 'icon'])
  const walk = (v: any, depth: number) => {
    if (depth > 6 || out.join(' ').length > 6000) return
    if (typeof v === 'string') { const t = v.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(); if (t.length > 1) out.push(t) }
    else if (Array.isArray(v)) v.forEach((x) => walk(x, depth + 1))
    else if (v && typeof v === 'object') Object.entries(v).forEach(([k, x]) => { if (!SKIP.has(k)) walk(x, depth + 1) })
  }
  if (lesson.meta?.mucTieu) out.push('MỤC TIÊU: ' + lesson.meta.mucTieu)
  ;(lesson.sections || []).forEach((sec) => { out.push('• ' + (sec.title || '')); (sec.cards || []).forEach((cd) => walk(cd, 0)) })
  return out.join('\n').slice(0, 6000)
}

// Model Gemini mới nhất (cập nhật 06/2026), ưu tiên loại mạnh & gói MIỄN PHÍ của Google AI Studio.
// Backend chỉ chuyển tiếp chuỗi model nên thêm/bớt ở đây là đủ.
const DEFAULT_GEMINI_MODEL = 'gemini-3.5-flash'
const GEMINI_MODELS: { id: string; label: string }[] = [
  { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash — mới & mạnh nhất dòng Flash (khuyến nghị)' },
  { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash-Lite — nhanh & tiết kiệm nhất' },
  { id: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (preview) — hiệu năng cao, chi phí thấp' },
  { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (preview) — suy luận sâu nhất' },
  { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash — ổn định, cân bằng' },
  { id: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite — ổn định, tiết kiệm' },
  { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro — ổn định, suy luận sâu' },
]
const num = (s?: string) => (s ? Number(String(s).match(/\d+/)?.[0]) : undefined)

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as VideoLectureCard
  const { lesson } = useLessonBuilder()
  const scenes = c.scenes || []
  const lop = num(lesson.meta.lop), chuong = num(lesson.meta.chuong), bai = num(lesson.meta.bai)
  const canvasSims = (lop && chuong && bai) ? SimulationLibrary.getSimsByLesson(lop, chuong, bai).filter((s) => s.kind === 'canvas') : []

  const [gen, setGen] = useState(false)
  const [msg, setMsg] = useState('')
  const [narrating, setNarrating] = useState<number | null>(null)
  // Cấu hình Gemini
  const [cfgOpen, setCfgOpen] = useState(false)
  const [hasKey, setHasKey] = useState(false)
  const [model, setModel] = useState(DEFAULT_GEMINI_MODEL)
  const [keyInput, setKeyInput] = useState('')
  const [savingCfg, setSavingCfg] = useState(false)
  const [bankVideos, setBankVideos] = useState<BankVideo[]>([])
  const [exporting, setExporting] = useState(false)
  const [veoBusy, setVeoBusy] = useState<number | null>(null)
  const [veoModel, setVeoModel] = useState(VEO_MODELS[0].id)

  useEffect(() => { aiSettingsApi.get().then((s) => { setHasKey(s.hasGeminiKey); setModel(s.geminiModel) }) }, [])
  useEffect(() => { videoApi.catalog(lop, chuong, bai).then(setBankVideos) }, [lop, chuong, bai])

  const setScenes = (next: LectureScene[]) => onChange({ scenes: next } as any)
  const patchScene = (i: number, p: Partial<LectureScene>) => setScenes(scenes.map((s, k) => (k === i ? { ...s, ...p } : s)))
  const move = (i: number, d: -1 | 1) => { const j = i + d; if (j < 0 || j >= scenes.length) return; const n = [...scenes];[n[i], n[j]] = [n[j], n[i]]; setScenes(n) }
  const del = (i: number) => setScenes(scenes.filter((_, k) => k !== i))
  // Chèn 1 cảnh vào SAU vị trí i (i=-1 → đầu danh sách). Mặc định cảnh chữ; kind='question' → cảnh câu hỏi.
  const insertAt = (i: number, kind: 'scene' | 'question' = 'scene') => {
    const blank: LectureScene = kind === 'question'
      ? { id: genId(), voiceover: '', caption: '', visualType: 'question', question: { prompt: '', options: ['', ''], correctIndex: 0 } }
      : { id: genId(), voiceover: '', caption: '', visualType: 'text', durationSec: 6 }
    const n = [...scenes]; n.splice(i + 1, 0, blank); setScenes(n)
  }
  const addScene = () => insertAt(scenes.length - 1, 'scene')
  // Prompt copy dán vào công cụ tạo video (Flow/AI Studio/…).
  const buildPrompt = (s: LectureScene) =>
    s.prompt || [s.setting, s.action,
      s.presenter ? `Giáo viên (người dẫn ảo) đang ${s.presenterAction || 'di chuyển, cử chỉ và tương tác với đối tượng trong cảnh'}` : '',
      s.caption].filter(Boolean).join('. ')
  const copyPrompt = async (s: LectureScene) => {
    try { await navigator.clipboard.writeText(buildPrompt(s)); setMsg('✅ Đã copy prompt cảnh — dán vào Flow/AI Studio để tạo clip.') }
    catch { setMsg('Không copy được — bạn chọn và sao chép thủ công.') }
  }

  // Đổi loại hình: khi chọn "câu hỏi" thì khởi tạo cấu trúc câu hỏi rỗng.
  const setVisual = (i: number, vt: LectureScene['visualType']) => {
    const p: Partial<LectureScene> = { visualType: vt }
    if (vt === 'question' && !scenes[i].question) p.question = { prompt: '', options: ['', ''], correctIndex: 0 }
    patchScene(i, p)
  }
  // Chèn ẢNH GIÁO VIÊN: đọc file → data URL (lưu thành file khi Lưu bài).
  const uploadImg = (i: number, file?: File | null) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => patchScene(i, { imageSrc: String(reader.result || '') })
    reader.readAsDataURL(file)
  }
  // Sửa câu hỏi của 1 cảnh.
  const patchQ = (i: number, p: Partial<NonNullable<LectureScene['question']>>) => {
    const q = scenes[i].question || { prompt: '', options: ['', ''], correctIndex: 0 }
    patchScene(i, { question: { ...q, ...p } })
  }

  const saveCfg = async () => {
    setSavingCfg(true)
    try {
      const r = await aiSettingsApi.save(keyInput !== '' ? keyInput : undefined, model)
      setHasKey(r.hasGeminiKey); setModel(r.geminiModel); setKeyInput('')
      setMsg('✅ Đã lưu cấu hình Gemini.')
    } catch { setMsg('Lưu cấu hình chưa thành công.') } finally { setSavingCfg(false) }
  }

  const generate = async () => {
    setGen(true); setMsg('')
    try {
      const simById = new Map(canvasSims.map((s) => [s.id, s] as const))
      const r = await videoApi.generateScript({
        title: lesson.meta.tieuDe || c.title || 'Bài giảng',
        source_text: extractLessonText(lesson),
        lop, chuong, bai,
        availableSims: canvasSims.map((s) => ({ id: s.id, type: s.type, title: s.title })),
      })
      const next: LectureScene[] = r.scenes.map((sc) => {
        const sim = sc.simId ? simById.get(sc.simId) : undefined
        const vt: LectureScene['visualType'] = sim ? 'sim'
          : sc.videoUrl ? 'video'
          : sc.imageSrc ? 'image'
          : sc.formula ? 'formula'
          : 'text'
        return {
          id: genId(),
          voiceover: sc.voiceover, caption: sc.caption,
          visualType: vt,
          simType: sim?.type, formula: sc.formula || undefined,
          imageSrc: sc.imageSrc || undefined, videoUrl: sc.videoUrl || undefined,
          durationSec: sc.durationSec || 6,
          setting: sc.setting || undefined, action: sc.action || undefined,
          presenter: sc.presenter || undefined, presenterAction: sc.presenterAction || undefined,
          prompt: sc.prompt || undefined,
        }
      })
      setScenes(next)
      setMsg(`✅ Đã tạo ${next.length} cảnh (model ${r.model} · khoá Gemini của bạn). Hãy duyệt & tạo giọng đọc.`)
    } catch (e) {
      const m = (e as Error)?.message || ''
      setMsg(m.includes('503') ? 'Chưa có khoá Gemini — mở "Cấu hình Gemini" để nhập khoá riêng của bạn.' : 'Tạo kịch bản chưa thành công, thử lại.')
    } finally { setGen(false) }
  }

  // Xuất MP4 hoàn chỉnh NGAY trong trình duyệt → tải file về (server không phải render/lưu).
  const exportMp4 = async () => {
    if (!scenes.length) { setMsg('Chưa có cảnh nào để xuất.'); return }
    setExporting(true); setMsg('Bắt đầu xuất MP4…')
    try {
      const blob = await exportScenesToMp4(scenes, { onProgress: setMsg })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `${(lesson.meta.tieuDe || 'video-bai-giang').replace(/[^\w-]+/g, '-')}.mp4`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60000)
      setMsg('✅ Đã xuất MP4 và tải về máy.')
    } catch (e) { setMsg('Xuất MP4 chưa thành công: ' + ((e as Error)?.message || '')) } finally { setExporting(false) }
  }

  // Sinh CLIP VEO cho 1 cảnh (chỉ cảnh minh hoạ). Dùng khoá Gemini GV → họ tự trả phí.
  const genVeo = async (i: number) => {
    const s = scenes[i]
    const prompt = [s.caption, s.voiceover].filter(Boolean).join('. ').trim()
      || `Minh hoạ cho bài "${lesson.meta.tieuDe || ''}"`
    setVeoBusy(i); setMsg('Đang gửi yêu cầu sinh clip Veo… (có thể mất 1–3 phút)')
    try {
      const { operation } = await veoApi.start(prompt, veoModel)
      // Poll tối đa ~6 phút.
      for (let t = 0; t < 45; t++) {
        await new Promise((r) => setTimeout(r, 8000))
        const r = await veoApi.result(operation, lesson.id)
        if (r.done && r.url) {
          patchScene(i, { visualType: 'video', videoUrl: r.url })
          setMsg('✅ Đã sinh clip Veo và chèn vào cảnh.')
          return
        }
        setMsg(`Đang dựng clip Veo… (${t * 8}s)`)
      }
      setMsg('Veo dựng lâu hơn dự kiến — thử lại sau.')
    } catch (e) {
      const m = (e as Error)?.message || ''
      setMsg(m.includes('503') ? 'Chưa có khoá Gemini — nhập ở "Cấu hình Gemini" để dùng Veo.' : 'Sinh clip Veo chưa thành công: ' + m)
    } finally { setVeoBusy(null) }
  }

  const narrate = async (i: number) => {
    const s = scenes[i]
    if (!s.voiceover.trim()) { setMsg('Cảnh chưa có lời đọc.'); return }
    setNarrating(i)
    try {
      const r = await narrateSection(lesson.id, `${c.id}-s${i}`, s.voiceover, 'Kore')
      patchScene(i, { audioUrl: r.url })
    } catch { setMsg('Tạo giọng đọc chưa thành công.') } finally { setNarrating(null) }
  }

  return (
    <div className="space-y-3">
      <div className="p-3 rounded-lg bg-violet-50 border border-violet-100 text-violet-700 text-sm flex items-center gap-2">
        <Clapperboard size={16} /> <span><strong>Video bài giảng tương tác</strong> — AI bám Yêu cầu cần đạt + tài liệu Word sinh kịch bản (mô phỏng/công thức/ảnh kho chuẩn + lời đọc + phụ đề). Chèn ảnh của bạn, video đã có, câu hỏi tương tác; tạo giọng đọc; xuất MP4. Chèn thẳng vào bài.</span>
      </div>

      {/* Cấu hình Gemini (BYO key) */}
      <div className="rounded-lg border border-gray-200">
        <button onClick={() => setCfgOpen((o) => !o)} className="w-full flex items-center gap-2 px-3 py-2 text-sm font-bold text-gray-700">
          <KeyRound size={15} /> Cấu hình Gemini {hasKey ? <span className="text-[11px] font-bold text-emerald-600">· đã có khoá</span> : <span className="text-[11px] text-amber-600">· chưa có khoá (cần nhập khoá Gemini riêng)</span>}
        </button>
        {cfgOpen && (
          <div className="px-3 pb-3 space-y-2">
            <div>
              <label className="text-[11px] font-bold text-gray-500 uppercase">Khoá API Gemini của bạn</label>
              <input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder={hasKey ? '•••••• (để trống = giữ nguyên)' : 'AIza...'}
                className="w-full p-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-violet-200" />
              <p className="text-[10px] text-gray-400 mt-0.5">Video AI <strong>bắt buộc dùng khoá Gemini riêng</strong> — bạn tự chịu chi phí với Google, <strong>không liên quan điểm hệ thống</strong>. Khoá lưu riêng cho tài khoản bạn.</p>

              {/* Hướng dẫn lấy API Key miễn phí */}
              <details className="mt-1 rounded-lg bg-violet-50/60 border border-violet-100">
                <summary className="cursor-pointer select-none px-2.5 py-1.5 text-[11px] font-bold text-violet-700">📘 Cách lấy API Key Gemini miễn phí (bấm để xem)</summary>
                <ol className="px-4 pb-2.5 pt-1 text-[11px] text-gray-600 list-decimal space-y-1">
                  <li>Truy cập <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-violet-600 font-semibold underline">aistudio.google.com/apikey</a> và đăng nhập bằng tài khoản Google.</li>
                  <li>Bấm <strong>“Create API key”</strong> (Tạo khoá API). Chọn dự án có sẵn hoặc để Google tự tạo dự án mới.</li>
                  <li>Khoá dạng <code className="px-1 bg-white rounded border border-gray-200">AIza…</code> hiện ra — bấm <strong>Copy</strong>.</li>
                  <li>Dán vào ô <em>“Khoá API Gemini của bạn”</em> ở trên, chọn Model rồi bấm <strong>Lưu</strong>.</li>
                </ol>
                <p className="px-4 pb-2.5 text-[10px] text-gray-400">Gói miễn phí của Google AI Studio đủ dùng cho soạn bài (giới hạn lượt/phút & lượt/ngày). Khoá là của riêng bạn — không chia sẻ cho người khác.</p>
              </details>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-[11px] font-bold text-gray-500 uppercase">Model</label>
                <select value={model} onChange={(e) => setModel(e.target.value)} className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white outline-none">
                  {!GEMINI_MODELS.some((m) => m.id === model) && model && <option value={model}>{model} (đang dùng)</option>}
                  {GEMINI_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
              <button onClick={saveCfg} disabled={savingCfg} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-50">
                {savingCfg ? <Loader size={15} className="animate-spin" /> : <Save size={15} />} Lưu
              </button>
            </div>
            {hasKey && <button onClick={async () => { await aiSettingsApi.save('', model); setHasKey(false); setMsg('Đã xoá khoá Gemini.') }} className="text-[11px] text-rose-500 hover:underline">Xoá khoá đã lưu</button>}
          </div>
        )}
      </div>

      <button onClick={generate} disabled={gen}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white font-bold hover:brightness-110 disabled:opacity-50">
        {gen ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />} {gen ? 'Đang sinh kịch bản…' : 'Tạo kịch bản bằng AI'}
      </button>
      {msg && <p className="text-xs text-gray-600">{msg}</p>}

      {/* Danh sách cảnh */}
      <div className="space-y-2">
        {scenes.map((s, i) => (
          <div key={s.id} className="rounded-lg border border-gray-200 p-2.5 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-black px-2 py-0.5 rounded bg-violet-50 text-violet-700">Cảnh {i + 1}</span>
              {s.visualType === 'question' && <span className="text-[10px] font-bold text-amber-600">câu hỏi</span>}
              {s.audioUrl && <span className="text-[10px] font-bold text-emerald-600">có giọng đọc</span>}
              <div className="flex-1" />
              <button onClick={() => copyPrompt(s)} title="Copy prompt dán vào Flow/AI Studio" className="p-1 text-gray-400 hover:text-violet-600"><Copy size={14} /></button>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === scenes.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown size={14} /></button>
              <button onClick={() => del(i)} className="p-1 text-rose-400 hover:text-rose-600"><Trash2 size={14} /></button>
            </div>
            <textarea value={s.voiceover} onChange={(e) => patchScene(i, { voiceover: e.target.value })} rows={2} placeholder="Lời thoại / lời đọc (voiceover)…"
              className="w-full p-2 border border-gray-200 rounded text-sm outline-none focus:ring-2 focus:ring-violet-200 resize-y" />
            <input value={s.caption} onChange={(e) => patchScene(i, { caption: e.target.value })} placeholder="Phụ đề trên màn hình…"
              className="w-full p-2 border border-gray-200 rounded text-sm outline-none focus:ring-2 focus:ring-violet-200" />

            {/* Storyboard: bối cảnh / diễn biến / người dẫn (để dựng video ở công cụ ngoài) */}
            {s.visualType !== 'question' && (
              <details className="rounded bg-gray-50/70 border border-gray-100">
                <summary className="cursor-pointer select-none px-2 py-1 text-[11px] font-bold text-gray-500">🎬 Storyboard cảnh (bối cảnh · diễn biến · người dẫn)</summary>
                <div className="px-2 pb-2 pt-1 space-y-1.5">
                  <input value={s.setting || ''} onChange={(e) => patchScene(i, { setting: e.target.value })} placeholder="Bối cảnh (vd: phòng thí nghiệm sáng…)"
                    className="w-full p-1.5 border border-gray-200 rounded text-xs outline-none" />
                  <input value={s.action || ''} onChange={(e) => patchScene(i, { action: e.target.value })} placeholder="Diễn biến (chuyện gì xảy ra trong cảnh)"
                    className="w-full p-1.5 border border-gray-200 rounded text-xs outline-none" />
                  <label className="flex items-center gap-1.5 text-[11px] text-gray-600">
                    <input type="checkbox" checked={!!s.presenter} onChange={(e) => patchScene(i, { presenter: e.target.checked })} /> Giáo viên xuất hiện
                  </label>
                  {s.presenter && (
                    <input value={s.presenterAction || ''} onChange={(e) => patchScene(i, { presenterAction: e.target.value })} placeholder="GV làm gì (di chuyển, chỉ tay, thao tác — không đứng im)"
                      className="w-full p-1.5 border border-gray-200 rounded text-xs outline-none" />
                  )}
                  <textarea value={s.prompt || ''} onChange={(e) => patchScene(i, { prompt: e.target.value })} rows={2} placeholder="Prompt tạo video (để trống = tự ghép từ bối cảnh + diễn biến)"
                    className="w-full p-1.5 border border-gray-200 rounded text-xs outline-none resize-y" />
                  <button onClick={() => copyPrompt(s)} className="text-[11px] text-violet-600 font-semibold inline-flex items-center gap-1"><Copy size={11} /> Copy prompt → dán vào Flow/AI Studio</button>
                </div>
              </details>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <select value={s.visualType} onChange={(e) => setVisual(i, e.target.value as any)} className="p-1.5 border border-gray-200 rounded text-xs bg-white outline-none">
                <option value="text">Chỉ chữ</option>
                <option value="formula">Công thức</option>
                <option value="sim">Mô phỏng</option>
                <option value="image">Ảnh</option>
                <option value="video">Video đã có</option>
                <option value="question">Câu hỏi tương tác</option>
              </select>
              {s.visualType === 'sim' && (
                <select value={s.simType || ''} onChange={(e) => patchScene(i, { simType: e.target.value })} className="flex-1 min-w-[160px] p-1.5 border border-gray-200 rounded text-xs bg-white outline-none">
                  <option value="">— chọn mô phỏng —</option>
                  {canvasSims.map((sm) => <option key={sm.id} value={sm.type}>{sm.title}</option>)}
                </select>
              )}
              {s.visualType === 'formula' && (
                <input value={s.formula || ''} onChange={(e) => patchScene(i, { formula: e.target.value })} placeholder="LaTeX (vd x=A\cos(\omega t))"
                  className="flex-1 min-w-[160px] p-1.5 border border-gray-200 rounded text-xs outline-none" />
              )}
              {s.visualType === 'image' && (
                <>
                  <input value={s.imageSrc && !s.imageSrc.startsWith('data:') ? s.imageSrc : ''} onChange={(e) => patchScene(i, { imageSrc: e.target.value })} placeholder="URL ảnh"
                    className="flex-1 min-w-[140px] p-1.5 border border-gray-200 rounded text-xs outline-none" />
                  <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-gray-100 text-gray-600 text-xs font-bold cursor-pointer hover:bg-gray-200">
                    <Upload size={12} /> Ảnh của tôi
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(i, e.target.files?.[0])} />
                  </label>
                  {s.imageSrc?.startsWith('data:') && <span className="text-[10px] text-emerald-600 font-bold">đã chọn ảnh</span>}
                </>
              )}
              {s.visualType === 'video' && (
                <>
                  <input value={s.videoUrl || ''} onChange={(e) => patchScene(i, { videoUrl: e.target.value })} placeholder="Link YouTube hoặc .mp4"
                    className="flex-1 min-w-[140px] p-1.5 border border-gray-200 rounded text-xs outline-none" />
                  {bankVideos.length > 0 && (
                    <select value="" onChange={(e) => { if (e.target.value) patchScene(i, { videoUrl: e.target.value }) }} className="p-1.5 border border-gray-200 rounded text-xs bg-white outline-none" title="Chọn từ kho video khoa học">
                      <option value="">— kho video —</option>
                      {bankVideos.map((v) => <option key={v.youtubeId} value={v.url}>{v.title}</option>)}
                    </select>
                  )}
                  <button onClick={() => genVeo(i)} disabled={veoBusy !== null} title="Sinh clip bằng Veo (tính phí trên khoá Gemini của bạn)"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-fuchsia-600 text-white text-xs font-bold hover:bg-fuchsia-700 disabled:opacity-50">
                    {veoBusy === i ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Tạo clip AI
                  </button>
                  {/* Cắt clip: chỉ phát/đoạn từ giây … đến giây … */}
                  <span className="inline-flex items-center gap-1 text-[11px] text-gray-500" title="Cắt đoạn video (giây)">
                    <Scissors size={12} />
                    <input type="number" min={0} value={s.clipStart ?? ''} onChange={(e) => patchScene(i, { clipStart: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })}
                      placeholder="từ" className="w-14 p-1 border border-gray-200 rounded text-xs outline-none" />
                    <span>→</span>
                    <input type="number" min={0} value={s.clipEnd ?? ''} onChange={(e) => patchScene(i, { clipEnd: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })}
                      placeholder="đến" className="w-14 p-1 border border-gray-200 rounded text-xs outline-none" />
                    <span>giây</span>
                  </span>
                </>
              )}
              {s.visualType !== 'question' && (
                <button onClick={() => narrate(i)} disabled={narrating === i}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-orange-500 text-white text-xs font-bold hover:bg-orange-600 disabled:opacity-50">
                  {narrating === i ? <Loader2 size={12} className="animate-spin" /> : <Mic size={12} />} {s.audioUrl ? 'Tạo lại' : 'Tạo giọng'}
                </button>
              )}
            </div>

            {/* Trình soạn câu hỏi tương tác (không tính thống kê) */}
            {s.visualType === 'question' && (
              <div className="rounded-lg bg-amber-50/60 border border-amber-100 p-2 space-y-1.5">
                <input value={s.question?.prompt || ''} onChange={(e) => patchQ(i, { prompt: e.target.value })} placeholder="Nội dung câu hỏi (có thể dùng $…$)"
                  className="w-full p-1.5 border border-gray-200 rounded text-xs outline-none" />
                {(s.question?.options || []).map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-1.5">
                    <input type="radio" name={`${s.id}-correct`} checked={s.question?.correctIndex === oi} onChange={() => patchQ(i, { correctIndex: oi })} title="Đáp án đúng" />
                    <input value={opt} onChange={(e) => patchQ(i, { options: (s.question!.options).map((o, k) => k === oi ? e.target.value : o) })}
                      placeholder={`Phương án ${String.fromCharCode(65 + oi)}`} className="flex-1 p-1.5 border border-gray-200 rounded text-xs outline-none" />
                    {(s.question?.options.length || 0) > 2 && (
                      <button onClick={() => patchQ(i, { options: s.question!.options.filter((_, k) => k !== oi), correctIndex: Math.max(0, (s.question!.correctIndex > oi ? s.question!.correctIndex - 1 : s.question!.correctIndex)) })}
                        className="p-1 text-rose-400 hover:text-rose-600"><Trash2 size={12} /></button>
                    )}
                  </div>
                ))}
                <button onClick={() => patchQ(i, { options: [...(s.question?.options || []), ''] })} className="text-[11px] text-amber-700 font-semibold inline-flex items-center gap-1"><Plus size={11} /> Thêm phương án</button>
                <p className="text-[10px] text-gray-400">Chọn ô tròn ở phương án đúng. Trả lời đúng mới qua; sai thì xem lại cảnh trước.</p>
              </div>
            )}

            {/* Chèn vào SAU cảnh này */}
            <div className="flex items-center gap-2 pt-0.5">
              <button onClick={() => insertAt(i, 'scene')} className="text-[11px] text-violet-600 font-semibold inline-flex items-center gap-1"><Plus size={11} /> Chèn cảnh sau</button>
              <button onClick={() => insertAt(i, 'question')} className="text-[11px] text-amber-600 font-semibold inline-flex items-center gap-1"><HelpCircle size={11} /> Chèn câu hỏi sau</button>
            </div>
          </div>
        ))}
        <button onClick={addScene} className="text-xs text-violet-600 font-semibold inline-flex items-center gap-1"><Plus size={13} /> Thêm cảnh</button>
      </div>

      {/* Cấu hình Veo (chỉ áp cho cảnh "Video đã có" khi bấm "Tạo clip AI") */}
      <div className="flex items-center gap-2 flex-wrap text-[11px] text-gray-500 bg-fuchsia-50/60 border border-fuchsia-100 rounded-lg px-2.5 py-1.5">
        <Sparkles size={13} className="text-fuchsia-600" />
        <span className="font-bold text-fuchsia-700">Clip AI (Veo)</span>
        <select value={veoModel} onChange={(e) => setVeoModel(e.target.value)} className="p-1 border border-gray-200 rounded text-[11px] bg-white outline-none">
          {VEO_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <span>Chỉ dùng cho cảnh minh hoạ — <strong>tính phí trên khoá Gemini của bạn</strong>, không đảm bảo chính xác công thức/kiến thức (đừng dùng cho phần lõi).</span>
      </div>

      {scenes.length > 0 && (
        <button onClick={exportMp4} disabled={exporting}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-violet-300 text-violet-700 font-bold hover:bg-violet-50 disabled:opacity-50">
          {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} {exporting ? 'Đang xuất MP4…' : 'Xuất MP4 (tải về máy)'}
        </button>
      )}
      <p className="text-[11px] text-gray-400">Mô phỏng/công thức/ảnh kho lấy từ hệ thống nên chính xác. Bấm "Tạo giọng" cho từng cảnh rồi Lưu bài. Xuất MP4 chạy ngay trên máy bạn (không tải nặng máy chủ); mô phỏng & video nhúng trong MP4 hiển thị dạng thẻ — xem tương tác đầy đủ ở trình phát trong bài.</p>
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as VideoLectureCard
  return <ScenePlayer scenes={c.scenes || []} />
}

export const videoLectureCardDef: CardDef = {
  type: 'video_lecture',
  label: 'Video bài giảng (AI)',
  description: 'AI sinh kịch bản bám YCCD: mô phỏng/ảnh/video/câu hỏi + lời đọc, phát trong bài & xuất MP4',
  icon: Clapperboard,
  color: 'bg-violet-50 text-violet-600',
  createDefault: (): VideoLectureCard => ({ id: genId(), type: 'video_lecture', title: 'Video bài giảng', scenes: [] }),
  Editor,
  Preview,
}
